import React, { useState } from 'react'
import { Box, CircularProgress, Stack, Typography, styled } from '@mui/material'

import { IFinancialAccount } from '@/types'
import { replaceNullValues } from '@/utils/common'
import { DataTable, Order } from '../UI/DataTable'
import { ErrorPage } from '@/pages/Error'
import SearchLogo from '@/assets/search.svg'
import { financialAccountColumns } from './columns'
import {
  AccountSortKey,
  DEFAULT_ACCOUNT_SORT_BY,
  DEFAULT_ACCOUNT_SORT_ORDER,
  sortAccounts,
} from './sortAccounts'
import { useFinancialAccounts } from './useFinancialAccounts'

// Wide enough for every header to sit on its line; a narrower card scrolls the table sideways.
const ACCOUNTS_MIN_WIDTH = '900px'

interface FinancialAccountsListProps {
  userId: string
  connectionId: string
}

export const FinancialAccountsList: React.FC<FinancialAccountsListProps> = ({
  userId,
  connectionId,
}) => {
  const { data: financialAccounts, isLoading, isError } = useFinancialAccounts(userId, connectionId)
  // One sort for both sections, so the salary and other accounts read in the same order.
  const [sortBy, setSortBy] = useState<AccountSortKey>(DEFAULT_ACCOUNT_SORT_BY)
  const [sortOrder, setSortOrder] = useState<Order>(DEFAULT_ACCOUNT_SORT_ORDER)

  const handleSort = (property: AccountSortKey, order: Order) => {
    setSortBy(property)
    setSortOrder(order)
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress size={25} sx={{ color: 'lightgray' }} />
      </Box>
    )
  }

  if (isError) {
    return (
      <ErrorPage
        title={'Unable to Fetch Financial Accounts'}
        message={
          "We couldn't retrieve financial accounts details at the moment due to invalid bank connection. Try refreshing the connection. If the issue persists, contact administrator."
        }
      />
    )
  }

  if (!financialAccounts || financialAccounts.length === 0) {
    return (
      <EmptyContainer>
        <Box component="img" src={SearchLogo} alt="Search Icon" my={3} />
        <Title>No Accounts Found</Title>
      </EmptyContainer>
    )
  }

  const safeAccounts = sortAccounts(
    replaceNullValues(financialAccounts) as IFinancialAccount[],
    sortBy,
    sortOrder
  )
  const salaryAccounts = safeAccounts.filter(account => account.isSalary)
  const otherAccounts = safeAccounts.filter(account => !account.isSalary)

  // Salary accounts are listed in a section of their own, above the rest.
  const renderSection = (title: string, accounts: IFinancialAccount[]) =>
    accounts.length > 0 && (
      <Box component="section" aria-label={title}>
        <Label mb={1}>{title}</Label>
        <DataTable<IFinancialAccount>
          rows={accounts}
          columns={financialAccountColumns}
          minWidth={ACCOUNTS_MIN_WIDTH}
          onSort={handleSort}
          sortBy={sortBy}
          sortOrder={sortOrder}
        />
      </Box>
    )

  return (
    <Stack gap={2} py={1}>
      {renderSection('Salary Account', salaryAccounts)}
      {renderSection('Other Accounts', otherAccounts)}
    </Stack>
  )
}

const Label = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: 'bold',
  fontFamily: 'Inter',
  color: '#344054',
}))

const EmptyContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
}))

const Title = styled(Typography)(({ theme }) => ({
  ...theme.typography.h6,
  fontSize: '0.9rem',
  paddingBottom: theme.spacing(3),
}))
